import styled from 'styled-components';

/**
 * Função helper para gerar estilos de variantes de botões
 * @param {string} variant - Tipo do botão (primary, danger, success, warning, secondary, info)
 * @param {boolean} disabled - Se o botão está desabilitado
 * @returns {string} Estilos CSS para o botão
 */
export const getVariantStyles = (variant, disabled) => {
    if (disabled) {
        return `
            background: #e0e0e0;
            color: #9e9e9e;
            cursor: not-allowed;
            pointer-events: none;
        `;
    }

    const variants = {
        primary: `
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            &:hover {
                box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
            }
        `,
        danger: `
            background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
            color: white;
            &:hover {
                box-shadow: 0 6px 16px rgba(231, 76, 60, 0.4);
            }
        `,
        success: `
            background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
            color: white;
            &:hover {
                box-shadow: 0 6px 16px rgba(39, 174, 96, 0.4);
            }
        `,
        warning: `
            background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
            color: white;
            &:hover {
                box-shadow: 0 6px 16px rgba(243, 156, 18, 0.4);
            }
        `,
        secondary: `
            background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
            color: white;
            &:hover {
                box-shadow: 0 6px 16px rgba(149, 165, 166, 0.4);
            }
        `,
        info: `
            background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
            color: white;
            &:hover {
                box-shadow: 0 6px 16px rgba(52, 152, 219, 0.4);
            }
        `,
    };

    return variants[variant] || variants.primary;
};

/**
 * Botão de voltar com ícone
 * Aceita prop 'color' para personalização por tema
 */
export const BackButton = styled.button`
    background: ${(props) =>
        props.color
            ? `${props.color}1A`
            : 'rgba(102, 126, 234, 0.1)'
    };
    border: none;
    border-radius: 12px;
    width: ${(props) => props.size === 'large' ? '48px' : '42px'};
    height: ${(props) => props.size === 'large' ? '48px' : '42px'};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${(props) => props.color || '#667eea'};
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
        background: ${(props) =>
            props.color
                ? `${props.color}33`
                : 'rgba(102, 126, 234, 0.2)'
        };
        transform: translateX(-4px);
    }

    &:active {
        transform: translateX(0);
    }
`;

/**
 * Botão de nova ação (Nova Aplicação, Novo Grupo, etc)
 * Aceita prop 'gradient' para personalização
 */
export const NewButton = styled.button`
    background: ${(props) =>
        props.gradient ||
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    };
    color: white;
    border: none;
    border-radius: 12px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
    box-shadow: ${(props) =>
        props.gradient?.includes('#e67e22')
            ? '0 4px 12px rgba(230, 126, 34, 0.3)'
            : '0 4px 12px rgba(102, 126, 234, 0.3)'
    };

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${(props) =>
            props.gradient?.includes('#e67e22')
                ? '0 6px 16px rgba(230, 126, 34, 0.4)'
                : '0 6px 16px rgba(102, 126, 234, 0.4)'
        };
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

/**
 * Botão de ação em tabelas
 * Aceita prop 'gradient' para personalização
 */
export const TableActionButton = styled.button`
    background: ${(props) =>
        props.gradient ||
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    };
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Mulish', sans-serif;
    transition: all 0.3s ease;
    box-shadow: ${(props) =>
        props.gradient?.includes('#e67e22')
            ? '0 2px 8px rgba(230, 126, 34, 0.3)'
            : '0 2px 8px rgba(102, 126, 234, 0.3)'
    };
    display: inline-flex;
    align-items: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${(props) =>
            props.gradient?.includes('#e67e22')
                ? '0 4px 12px rgba(230, 126, 34, 0.4)'
                : '0 4px 12px rgba(102, 126, 234, 0.4)'
        };
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

/**
 * Botão de ação com variantes (usado em formulários e action bars)
 * Aceita props: variant, disabled
 */
export const ActionButton = styled.button`
    border: none;
    border-radius: 12px;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
    font-family: 'Mulish', sans-serif;
    ${({ variant, disabled }) => getVariantStyles(variant, disabled)}

    &:hover {
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }
`;

// Alias para compatibilidade
export const FormActionButton = ActionButton;

/**
 * Botão de ação do header (exportado do layout anterior)
 */
export const HeaderActionButton = styled.button`
    background: ${(props) => {
        if (props.variant === 'secondary') {
            return 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
        }
        return props.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }};
    color: white;
    border: none;
    border-radius: 12px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;
